import { ReactNode, SyntheticEvent, useCallback } from 'react';

import {
  AutocompleteChangeDetails,
  AutocompleteGetTagProps,
  AutocompleteHighlightChangeReason,
  AutocompleteProps,
  AutocompleteRenderOptionState,
  FilterOptionsState,
  Value,
} from '@material-ui/lab';

import { PaginatedListItem } from '../../hooks';
import { LoadingChip } from './LoadingChip';

export type AutocompleteTypeConversionMethod =
  | 'getOptionLabel'
  | 'getOptionSelected'
  | 'renderOption'
  | 'getOptionDisabled'
  | 'groupBy'
  | 'onHighlightChange';

export type AutocompleteTypeConversionMethods<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
> = Pick<
  AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>,
  AutocompleteTypeConversionMethod
> & {
  getOptionLabel?: (option: T) => string;
  filterOptions?: (options: T[], state: FilterOptionsState<string>) => string[];
  renderTags?: (
    options: T[],
    getTagProps: AutocompleteGetTagProps,
  ) => ReactNode;
  onChange?: (
    event: SyntheticEvent<EventTarget>,
    value: Value<string, Multiple, DisableClearable, FreeSolo>,
    reason: string,
    details?: AutocompleteChangeDetails<T>,
  ) => void;
};

export const useAutocompleteTypeConversionMethods = <
  T extends PaginatedListItem = PaginatedListItem,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  options,
  loading,
  ...overrides
}: AutocompleteTypeConversionMethods<
  T,
  Multiple,
  DisableClearable,
  FreeSolo
> & {
  options: T[];
  loading?: boolean;
}): AutocompleteTypeConversionMethods<
  string,
  Multiple,
  DisableClearable,
  FreeSolo
  // getOptionLabel is optional as input, but always returned from this hook
> & { getOptionLabel: (option: string) => string } => {
  const {
    getOptionLabel: getOptionLabelOverride,
    getOptionDisabled: getOptionDisabledOverride,
    renderOption: renderOptionOverride,
    getOptionSelected: getOptionSelectedOverride,
    groupBy: groupByOverride,
    onHighlightChange: onHighlightChangeOverride,
    filterOptions: filterOptionsOverride,
    renderTags: renderTagsOverride,
    onChange: onChangeOverride,
  } = overrides;

  const getOption = useCallback(
    (option: string) => {
      return options.find(o => o.name === option) || ({ name: option } as T);
    },
    [options],
  );

  const getOptionLabel = useCallback(
    (name: string) => {
      const option = getOption(name);
      return getOptionLabelOverride?.(option) || option?.displayName || name;
    },
    [getOption, getOptionLabelOverride],
  );

  const getOptionDisabled = useCallback(
    (name: string) => {
      const option = getOption(name);
      return getOptionDisabledOverride?.(option) ?? false;
    },
    [getOptionDisabledOverride, getOption],
  );

  const renderOption = useCallback(
    (name: string, props: AutocompleteRenderOptionState) => {
      const option = getOption(name);
      return renderOptionOverride?.(option, props) ?? getOptionLabel(name);
    },
    [renderOptionOverride, getOption, getOptionLabel],
  );

  const getOptionSelected = useCallback(
    (name: string, selectedName: string) => {
      const option = getOption(name);
      const selectedOption = getOption(selectedName);
      return getOptionSelectedOverride?.(option, selectedOption) ?? false;
    },
    [getOptionSelectedOverride, getOption],
  );

  const groupBy = useCallback(
    (name: string) => {
      const option = getOption(name);
      return groupByOverride?.(option) || '';
    },
    [groupByOverride, getOption],
  );

  const onHighlightChange = useCallback(
    (
      event: SyntheticEvent<EventTarget>,
      name: string | null,
      reason: AutocompleteHighlightChangeReason,
    ) => {
      return onHighlightChangeOverride?.(
        event,
        name ? getOption(name) : null,
        reason,
      );
    },
    [onHighlightChangeOverride, getOption],
  );

  const filterOptions = useCallback(
    (_names: string[], props: FilterOptionsState<string>) => {
      return filterOptionsOverride?.(options, props) || [];
    },
    [filterOptionsOverride, options],
  );

  const renderTags = useCallback(
    (selected: string[], getTagProps: AutocompleteGetTagProps) =>
      renderTagsOverride?.(selected.map(getOption), getTagProps) ??
      // Override default rendering with LoadingChip to avoid flickering while fetching displayNames
      selected.map((option: string, index: number) => {
        const tagProps = getTagProps({ index });
        const loadingTag = loading && !options.some(o => o.name === option);
        return (
          <LoadingChip
            {...tagProps}
            key={option}
            size="small"
            loading={loadingTag}
            label={getOptionLabel(option)}
          />
        );
      }),
    [renderTagsOverride, getOption, options, loading, getOptionLabel],
  );

  const onChange = useCallback(
    (
      event: SyntheticEvent<EventTarget>,
      value: Value<string, Multiple, DisableClearable, FreeSolo>,
      reason: string,
      details: Partial<AutocompleteChangeDetails<string>> = {},
    ) => {
      const singleValue = typeof value === 'string' ? value : '';
      const option = getOption(singleValue);

      return onChangeOverride?.(event, value, reason, {
        ...details,
        option,
      });
    },
    [onChangeOverride, getOption],
  );

  return {
    getOptionLabel,
    renderTags,
    onChange: onChangeOverride ? onChange : undefined,
    getOptionDisabled: getOptionDisabledOverride
      ? getOptionDisabled
      : undefined,
    renderOption: renderOptionOverride ? renderOption : undefined,
    getOptionSelected: getOptionSelectedOverride
      ? getOptionSelected
      : undefined,
    groupBy: groupByOverride ? groupBy : undefined,
    onHighlightChange: onHighlightChangeOverride
      ? onHighlightChange
      : undefined,
    filterOptions: filterOptionsOverride ? filterOptions : undefined,
  };
};
